/**
 * URL format helpers for native entity IDs.
 *
 * Internal format:  "fb:t:123"  (colon-separated, used by ID mapping + APIs)
 * URL format:       "fb-t-123"  (dash-separated, used in URLs for SEO)
 *
 * Inbound URL params go through urlSegmentToId() to restore the colon format
 * before reaching any downstream logic. Outbound URL building goes through
 * idToUrlSegment() so emitted URLs are SEO-friendly.
 */

/** Param names in route configs that hold native entity IDs. */
export const ID_PARAM_NAMES = new Set(["competitionId", "matchId", "teamId", "playerId"]);

const URL_PATTERN = /^([a-z]+)-([a-z])-(\d+)$/;
const INTERNAL_PATTERN = /^([a-z]+):([a-z]):(\d+)$/;

/** Convert "fb:t:123" → "fb-t-123". Returns input unchanged if it doesn't match. */
export function idToUrlSegment(id: string): string {
    const match = id.match(INTERNAL_PATTERN);
    if (!match) return id;
    return `${match[1]}-${match[2]}-${match[3]}`;
}

/**
 * Convert "fb-t-123" → "fb:t:123". Accepts either format (backward compat).
 * Returns input unchanged if it doesn't match.
 */
export function urlSegmentToId(segment: string): string {
    if (INTERNAL_PATTERN.test(segment)) return segment;
    const match = segment.match(URL_PATTERN);
    if (!match) return segment;
    return `${match[1]}:${match[2]}:${match[3]}`;
}

/**
 * Rewrite internal-format IDs ("fb:t:123", also percent-encoded "fb%3At%3A123") in a URL path to the
 * dash form. Returns the rebuilt path, or null when no segment changed.
 */
export function canonicalizeIdPath(pathname: string): string | null {
    let changed = false;
    const segments = pathname.split("/").map((segment) => {
        let decoded = segment;
        try {
            decoded = decodeURIComponent(segment);
        } catch {
            // malformed escape — keep the raw segment
        }
        if (!INTERNAL_PATTERN.test(decoded)) return segment;
        changed = true;
        return idToUrlSegment(decoded);
    });
    return changed ? segments.join("/") : null;
}
